import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as RoutePath from "../../shared/utils/routeLink";
import * as Variable from "../../shared/utils/variables";
import * as ActionTypes from "../../store/actions/actionTypes";
import "./ProfileDetail.scss";
import { CloseEyeIcon, OpenEyeIcon, UserCircleIconFilled } from "../../shared/component/svg/Icons";
import { toast } from "react-toastify";
import makeRequest from "../../shared/utils/request";
import { generateRequestOptions } from "../../shared/utils/apiEndPoints";

const ProfileDetail = ({ ENV_NAME, isAuthenticated, setLoginModalShow, ...props }) => {
	const [errorMessage, setErrorMessage] = useState(null);
	const [errorMessagePwd, setErrorMessagePwd] = useState(null);
	const [form, setForm] = useState({});
	const [isShowPassword, setIsShowPassword] = useState(false);
	const [isShowNewPassword, setIsShowNewPassword] = useState(false);
	const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	// Redirect to the homepage if we're not logged in
	useEffect(() => {
		if (!isAuthenticated) props.history.push(RoutePath.ROOT);
	}, [!isAuthenticated]); // eslint-disable-line

	// Load the data
	useEffect(() => {
		getProfileDetail();
	}, []); // eslint-disable-line

	// Get profile detail using token (logged in user details API)
	const getProfileDetail = async () => {
		if (isLoading) return;
		setIsLoading(true);
		let res = await makeRequest(generateRequestOptions("getUserViaToken"));
		if (res.code === 200) {
			let data = res.data;
			setForm({
				firstName: data?.first_name || "",
				lastName: data?.last_name || "",
				email: data?.email || "",
				username: data?.username || "",
			});
		} else {
			toast.error(res.message || Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME]);
		}
		setIsLoading(false);
	};

	const onChangeField = (e) => {
		setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
	};

	// Validate the form, check the main form is filled (firstname, lastname, email, username)
	const isMainFormFilled = () => {
		return (
			true && // So that it'll return a boolean
			form?.firstName &&
			form?.lastName &&
			form?.email &&
			form?.username
		);
	};

	const isOptionalFormFilled = () => {
		// If none is filled return true (not filled at all)
		if (!form?.curPassword && !form?.newPassword && !form?.confirmPass) return true;
		return true && form?.curPassword && form?.newPassword && form?.confirmPass;
	};

	const saveChanges = async () => {
		// Validations
		let mainFormFilled = isMainFormFilled();
		let optionalFormFilled = isOptionalFormFilled();
		if (!mainFormFilled) {
			return setErrorMessage(`${Variable.PROFILE[ENV_NAME]}: ${Variable.PLEASE_FILL_NECESSARY_FORM[ENV_NAME]}`);
		}
		if (!optionalFormFilled) {
			return setErrorMessagePwd(
				`${Variable.PASSWORD[ENV_NAME]}: ${Variable.PLEASE_FILL_NECESSARY_FORM[ENV_NAME]}`
			);
		}
		if (form?.newPassword !== form?.confirmPass) {
			return setErrorMessagePwd(Variable.PASSWORD_NOT_MATCHED[ENV_NAME]);
		}

		setErrorMessage(false);
		setErrorMessagePwd(false);
		setIsProcessing(true);

		let res = await makeRequest({
			...generateRequestOptions("editUser"),
			data: {
				first_name: form?.firstName,
				last_name: form?.lastName,
				email: form?.email,
			},
			json: true,
		});
		if (res) {
			toast.success(Variable.PROFILE_DETAIL_CHANGED[ENV_NAME]);
		} else {
			setErrorMessage(readErrorMsg(Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME]));
		}

		if (form?.curPassword && form?.newPassword && form?.confirmPass) {
			let res = await makeRequest({
				...generateRequestOptions("changePassword"),
				data: {
					old_password: form?.curPassword,
					password: form?.newPassword,
					password_confirm: form?.confirmPass,
				},
				json: true,
			});
			if (res?.code === 200) {
				toast.success(Variable.PASSWORD_CHANGED[ENV_NAME]);
			} else {
				setErrorMessagePwd(readPwdErrorMsg(res));
			}
		}

		setIsProcessing(false);
	};

	const readErrorMsg = (res) => {
		if (!res?.detail?.errors) return res?.message || Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME];
		return res?.detail?.errors?.map((i, idx) => <div key={`errormsg-${idx}`}>{`${i?.field}: ${i?.message}`}</div>);
	};

	const readPwdErrorMsg = (res) => {
		if (!res?.detail) return res?.message || Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME];
		return Object.keys(res?.detail)?.map((i, idx) => (
			<div key={`pwderrormsg-${idx}`}>{`${i}: ${res?.detail?.[i]}`}</div>
		));
	};

	return (
		<div className="profile-details">
			<div className="ca-form-cont">
				<div className="flex-all-center flex-column mb-4rem">
					<UserCircleIconFilled width="12rem" height="12rem" fill="#204F8D" />
					<h4 className="mt-1p6rem">
						<strong>{Variable.EDIT_PROFILE[ENV_NAME]}</strong>
					</h4>
				</div>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						saveChanges();
					}}
					autoComplete="new-password"
				>
					<h4 className="mb-md">
						<strong>{Variable.EDIT_PROFILE[ENV_NAME]}</strong>
					</h4>
					<div className="form-group">
						<label htmlFor="login" className="font-weight-medium">
							{Variable.FIRST_NAME[ENV_NAME]}
						</label>
						<input
							id="firstName"
							type="text"
							className={`form-control ${errorMessage ? "border-danger" : ""}`}
							name="firstName"
							value={form?.firstName || ""}
							onChange={onChangeField}
							placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.FIRST_NAME[ENV_NAME]}`}
							disabled={isLoading || isProcessing}
						/>
					</div>
					<div className="form-group">
						<label htmlFor="login" className="font-weight-medium">
							{Variable.LAST_NAME[ENV_NAME]}
						</label>
						<input
							id="lastName"
							type="text"
							className={`form-control ${errorMessage ? "border-danger" : ""}`}
							name="lastName"
							value={form?.lastName || ""}
							onChange={onChangeField}
							placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.LAST_NAME[ENV_NAME]}`}
							disabled={isLoading || isProcessing}
						/>
					</div>
					<div className="form-group">
						<label htmlFor="login" className="font-weight-medium">
							{Variable.EMAIL[ENV_NAME]}
						</label>
						<input
							id="email"
							type="email"
							className={`form-control ${errorMessage ? "border-danger" : ""}`}
							name="email"
							value={form?.email || ""}
							onChange={onChangeField}
							placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.EMAIL[ENV_NAME]}`}
							disabled={isLoading || isProcessing}
						/>
					</div>
					<div className="form-group mb-4rem">
						<label htmlFor="login" className="font-weight-medium">
							{Variable.USERNAME[ENV_NAME]}
						</label>
						<input
							id="username"
							type="text"
							className="form-control"
							name="username"
							value={form?.username || ""}
							onChange={() => {}}
							placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.USERNAME[ENV_NAME]}`}
							disabled
						/>
					</div>
					<h4 className="mb-md">
						<strong>{Variable.CHANGE_PASSWORD[ENV_NAME]}</strong>
					</h4>
					<div className="form-group">
						<label htmlFor="password" className="font-weight-medium">
							{Variable.CURRENT_PASSWORD[ENV_NAME]}
						</label>
						<div className="position-relative">
							<input
								id="curPassword"
								type={isShowPassword ? "text" : "password"}
								className={`form-control ${errorMessagePwd ? "border-danger" : ""}`}
								name="curPassword"
								value={form?.curPassword || ""}
								onChange={onChangeField}
								placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.CURRENT_PASSWORD[ENV_NAME]}`}
								autoComplete="new-password"
								disabled={isLoading || isProcessing}
							/>
							<div
								className={`pwd-eye cursorPointer ${!isShowPassword ? "active" : ""}`}
								onClick={() => setIsShowPassword((p) => !p)}
							>
								{isShowPassword ? (
									<OpenEyeIcon width="1.6rem" height="1.6rem" stroke="#193560" />
								) : (
									<CloseEyeIcon width="1.6rem" height="1.6rem" stroke="#BEC4CF" />
								)}
							</div>
						</div>
					</div>
					<div className="form-group">
						<label htmlFor="password" className="font-weight-medium">
							{Variable.NEW_PASSWORD[ENV_NAME]}
						</label>
						<div className="position-relative">
							<input
								id="newPassword"
								type={isShowNewPassword ? "text" : "password"}
								className={`form-control ${errorMessagePwd ? "border-danger" : ""}`}
								name="newPassword"
								value={form?.newPassword || ""}
								onChange={onChangeField}
								placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.NEW_PASSWORD[ENV_NAME]}`}
								autoComplete="new-password"
								disabled={isLoading || isProcessing}
							/>
							<div
								className={`pwd-eye cursorPointer ${!isShowNewPassword ? "active" : ""}`}
								onClick={() => setIsShowNewPassword((p) => !p)}
							>
								{isShowNewPassword ? (
									<OpenEyeIcon width="1.6rem" height="1.6rem" stroke="#193560" />
								) : (
									<CloseEyeIcon width="1.6rem" height="1.6rem" stroke="#BEC4CF" />
								)}
							</div>
						</div>
					</div>
					<div className="form-group">
						<label htmlFor="password" className="font-weight-medium">
							{Variable.CONFIRM_PASSWORD[ENV_NAME]}
						</label>
						<div className="position-relative">
							<input
								id="confirmPass"
								type={isShowConfirmPassword ? "text" : "password"}
								className={`form-control ${errorMessagePwd ? "border-danger" : ""}`}
								name="confirmPass"
								value={form?.confirmPass || ""}
								onChange={onChangeField}
								placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.CONFIRM_PASSWORD[ENV_NAME]}`}
								autoComplete="new-password"
								disabled={isLoading || isProcessing}
							/>
							<div
								className={`pwd-eye cursorPointer ${!isShowConfirmPassword ? "active" : ""}`}
								onClick={() => setIsShowConfirmPassword((p) => !p)}
							>
								{isShowConfirmPassword ? (
									<OpenEyeIcon width="1.6rem" height="1.6rem" stroke="#193560" />
								) : (
									<CloseEyeIcon width="1.6rem" height="1.6rem" stroke="#BEC4CF" />
								)}
							</div>
						</div>
					</div>
					{errorMessage && <div className="error-msg mt-1p6rem mb-0">{errorMessage}</div>}
					{errorMessagePwd && <div className="error-msg mt-1p6rem mb-0">{errorMessagePwd}</div>}
					<button
						type="submit"
						className="btn btn-cta-primary btn-block w-100"
						disabled={isProcessing || !isMainFormFilled() || !isOptionalFormFilled()}
					>
						{isProcessing ? (
							<div className="spinner-border" role="status">
								<span className="visually-hidden">{Variable.LOADING_LABEL[ENV_NAME]}</span>
							</div>
						) : (
							Variable.SAVE_PROFILE[ENV_NAME]
						)}
					</button>
				</form>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	return { isAuthenticated: state.auth.isAuthenticated, ENV_NAME: state.auth.selectedEnvironment || "bhs" };
};

const mapStateToDispatch = (dispatch) => {
	return {
		setLoginModalShow: (payload = true) => {
			if (payload) {
				dispatch({
					type: ActionTypes.OPEN_LOGIN_MODAL,
				});
			} else {
				dispatch({
					type: ActionTypes.CLOSE_LOGIN_MODAL,
				});
			}
		},
	};
};

export default connect(mapStateToProps, mapStateToDispatch)(withRouter(ProfileDetail));
